import React, { useState, useRef, useEffect } from 'react';
import { ExternalLink, Zap, Lock, Cpu, CreditCard, FileText } from 'lucide-react';
import { mockData } from '../data/mock';

const ProductsSection = () => {
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const categoryIcons = {
    'Accessibility': Zap,
    'Content Management': FileText,
    'AI Solutions': Cpu,
    'Payments': CreditCard
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSelectedProduct((prev) => (prev + 1) % mockData.products.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="products" ref={sectionRef} className="py-20 bg-gradient-to-br from-slate-50 to-blue-50/30 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-r from-blue-200/20 to-indigo-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-r from-indigo-200/20 to-blue-200/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <div className="inline-flex items-center space-x-2 text-sm font-medium text-blue-600 bg-blue-100/80 px-4 py-2 rounded-full mb-4">
            <Cpu size={16} />
            <span>Our Products</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Innovative
            </span>
            <br />
            Product Suite
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Cutting-edge solutions designed specifically for government, enterprise, and organizations requiring the highest standards of accessibility, security, and compliance.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Product Navigation */}
          <div className={`space-y-4 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
          }`}>
            {mockData.products.map((product, index) => {
              const CategoryIcon = categoryIcons[product.category];
              
              return (
                <div
                  key={index}
                  className={`relative cursor-pointer transition-all duration-500 transform ${
                    selectedProduct === index ? 'scale-105' : 'hover:scale-102'
                  }`}
                  onClick={() => setSelectedProduct(index)}
                >
                  <div className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
                    selectedProduct === index
                      ? 'bg-white border-blue-500 shadow-xl shadow-blue-500/20'
                      : 'bg-white/70 border-gray-200 hover:border-blue-300 hover:shadow-lg'
                  }`}>
                    <div className="flex items-start space-x-4">
                      {/* Product Logo */}
                      <div className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden transition-all duration-300 ${
                        selectedProduct === index ? 'ring-4 ring-blue-200' : ''
                      }`}>
                        <img
                          src={product.logo}
                          alt={product.name}
                          className="w-full h-full object-contain bg-white p-2"
                        />
                      </div>

                      <div className="flex-1">
                        {/* Category */}
                        <div className="flex items-center space-x-2 mb-2">
                          <CategoryIcon size={14} className="text-blue-500" />
                          <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                            {product.category}
                          </span>
                        </div>

                        {/* Product Name */}
                        <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${
                          selectedProduct === index ? 'text-blue-600' : 'text-gray-900'
                        }`}>
                          {product.name}
                        </h3>

                        {/* Short Description */}
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {product.description.length > 100 
                            ? `${product.description.substring(0, 100)}...`
                            : product.description
                          }
                        </p>
                      </div>

                      {/* Selection Indicator */}
                      <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        selectedProduct === index ? 'bg-blue-500' : 'bg-gray-300'
                      }`}></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected Product Details */}
          <div className={`lg:sticky lg:top-24 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
          }`}>
            <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100 relative overflow-hidden">
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-indigo-50/50 to-blue-50/50 opacity-0 animate-pulse"></div>
              
              <div className="relative z-10">
                {/* Product Header */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden ring-4 ring-blue-200">
                    <img
                      src={mockData.products[selectedProduct].logo}
                      alt={mockData.products[selectedProduct].name}
                      className="w-full h-full object-contain bg-white p-3"
                    />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      {React.createElement(categoryIcons[mockData.products[selectedProduct].category], {
                        size: 16,
                        className: "text-blue-500"
                      })}
                      <span className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                        {mockData.products[selectedProduct].category}
                      </span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">
                      {mockData.products[selectedProduct].name}
                    </h3>
                  </div>
                </div>

                {/* Description */}
                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                  {mockData.products[selectedProduct].description}
                </p>

                {/* Features */}
                <div className="space-y-4 mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Key Features</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {mockData.products[selectedProduct].features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-blue-50/50 rounded-lg transition-all duration-300 hover:bg-blue-100/50 transform hover:scale-105"
                      >
                        <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="flex space-x-4">
                  <button 
                    onClick={() => window.open(mockData.hero.calendlyLink, '_blank')}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
                  >
                    Learn More
                  </button>
                  <button className="p-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-xl transition-all duration-300 group">
                    <ExternalLink size={20} className="transition-transform group-hover:scale-110" />
                  </button>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-indigo-400/20 to-blue-400/20 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;